// Drives the in-frame loading indicator. FRAME_STATUS is the canonical vocabulary —
// forge callers import from here instead of writing literal strings, and the lookup
// table below references the same names. This concentrates both the "what string is
// written" and "how the moon responds to it" concerns in one file.
//
// Each table entry maps a statusMessage (literal or regex for dynamics) to a
// moon-progress slice: the moon advances from startCap toward cap linearly over
// budgetSec, holds at cap when the budget is overrun, and snaps forward when the
// next statusMessage arrives. budgetSec is a forge-side concern — tuning the
// loading UX doesn't require touching studio.

export const FRAME_STATUS = {
	GENERATING_DESIGN: 'generating design',
	EDITING_DESIGN: 'editing design',
	FINALIZING_DESIGN: 'finalizing design',
	CONVERTING_IMAGE_TO_HTML: 'converting image to HTML',
	CONVERTING_DESIGN: 'converting design',
	UPLOADING_IMAGE: 'uploading image',
	RETRIEVING_REFERENCE_DATA: 'retrieving reference data',
	RETRYING_GENERATION: 'retrying generation...',
	DETERMINING_DESIGN_DIMENSIONS: 'determining design width and height',
	EXPLORING_DESIGN_SPACE: 'exploring design space',
	WAITING_ON_FLOW_ANCHOR: 'waiting on flow anchor',
	generatingImages: (n: number): string =>
		n === 1 ? 'generating 1 image' : `generating ${n} images`,
	retryingGeneration: (attempt: number, total: number): string =>
		`retrying generation: try ${attempt} of ${total}`,
	downloadingImage: (i: number, total: number): string => `downloading image ${i} of ${total}`,
} as const;

// Regex mirrors of the dynamic factories above — kept adjacent so format drift
// is impossible to miss: change the factory, change the regex.
const GENERATING_IMAGES_RE = /^generating (\d+ images?|1 image)$/;
const RETRYING_GENERATION_RE = /^retrying generation: try \d+ of \d+$/;
const DOWNLOADING_IMAGE_RE = /^downloading image \d+ of \d+$/;

export interface FrameStatusConfig {
	startCap: number;
	cap: number;
	budgetSec: number;
}

interface FrameStatusEntry {
	match: string | RegExp;
	config: FrameStatusConfig;
}

export const FRAME_STATUS_TABLE: FrameStatusEntry[] = [
	{ match: FRAME_STATUS.GENERATING_DESIGN, config: { startCap: 0, cap: 0.8, budgetSec: 100 } },
	{ match: FRAME_STATUS.EDITING_DESIGN, config: { startCap: 0, cap: 0.8, budgetSec: 100 } },
	{
		match: FRAME_STATUS.CONVERTING_IMAGE_TO_HTML,
		config: { startCap: 0, cap: 0.8, budgetSec: 100 },
	},
	{ match: FRAME_STATUS.CONVERTING_DESIGN, config: { startCap: 0, cap: 0.8, budgetSec: 100 } },
	{
		match: FRAME_STATUS.RETRIEVING_REFERENCE_DATA,
		config: { startCap: 0, cap: 0.05, budgetSec: 3 },
	},
	{ match: FRAME_STATUS.UPLOADING_IMAGE, config: { startCap: 0, cap: 0.1, budgetSec: 5 } },
	{
		match: FRAME_STATUS.DETERMINING_DESIGN_DIMENSIONS,
		config: { startCap: 0, cap: 0.1, budgetSec: 3 },
	},
	{ match: DOWNLOADING_IMAGE_RE, config: { startCap: 0.1, cap: 0.7, budgetSec: 30 } },
	{ match: FRAME_STATUS.EXPLORING_DESIGN_SPACE, config: { startCap: 0, cap: 0.8, budgetSec: 100 } },
	{ match: FRAME_STATUS.WAITING_ON_FLOW_ANCHOR, config: { startCap: 0, cap: 0.1, budgetSec: 30 } },
	{ match: FRAME_STATUS.RETRYING_GENERATION, config: { startCap: 0.05, cap: 0.8, budgetSec: 100 } },
	{ match: RETRYING_GENERATION_RE, config: { startCap: 0.05, cap: 0.8, budgetSec: 100 } },
	{ match: GENERATING_IMAGES_RE, config: { startCap: 0.8, cap: 0.9, budgetSec: 20 } },
	{ match: FRAME_STATUS.FINALIZING_DESIGN, config: { startCap: 0.9, cap: 1.0, budgetSec: 5 } },
];

export const DEFAULT_FRAME_STATUS_CONFIG: FrameStatusConfig = {
	startCap: 0,
	cap: 0.8,
	budgetSec: 120,
};

// Returns the config for a given statusMessage. Unrecognized non-empty messages
// log a dev warning so drift in the backend vocabulary is visible immediately.
export const getFrameStatusConfig = (statusMessage: string): FrameStatusConfig => {
	for (const entry of FRAME_STATUS_TABLE) {
		const matched =
			typeof entry.match === 'string'
				? entry.match === statusMessage
				: entry.match.test(statusMessage);
		if (matched) return entry.config;
	}
	if (statusMessage) {
		console.warn(
			`[frame_status] unrecognized statusMessage "${statusMessage}" — add to FRAME_STATUS_TABLE`
		);
	}
	return DEFAULT_FRAME_STATUS_CONFIG;
};
